import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Search, Eye, Check, X, Trash2, Filter } from 'lucide-react';

const ManageBookingsNew = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [stats, setStats] = useState({});

  const [flights, setFlights] = useState({});

  useEffect(() => {
    fetchFlights().then(() => {
      fetchBookings();
    });
    fetchStats();
  }, []);

  const fetchFlights = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/flights`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Créer un map des vols par ID
        const flightsMap = {};
        data.forEach(f => { flightsMap[f.id] = f; });
        setFlights(flightsMap);
      }
    } catch (error) {
      console.error('Erreur flights:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/status?status=${encodeURIComponent(newStatus)}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        fetchBookings();
        fetchStats();
        alert('Statut mis à jour');
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réservation ?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        fetchBookings();
        fetchStats();
        alert('Réservation supprimée');
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'En attente': return '#e6a700';
      case 'Confirmée': return '#2c5f7d';
      case 'Payée': return '#28a745';
      case 'Annulée': return '#c41e3a';
      default: return '#666';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      stay: 'Séjour',
      event: 'Événement',
      afterwork: 'After-Work',
      flight: 'Vol',
      vol: 'Vol'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      stay: '#28a745',
      event: '#e6a700',
      afterwork: '#9c27b0',
      flight: '#2c5f7d',
      vol: '#2c5f7d'
    };
    return colors[type] || '#666';
  };

  // Normaliser les réservations pour inclure les vols (ancien format)
  const normalizedBookings = bookings.map(b => {
    // Si c'est une réservation de vol (ancien format avec flight_id)
    if (b.flight_id && !b.booking_type) {
      const flight = flights[b.flight_id];
      const flightTitle = flight?.title || 'Vol';
      const flightDate = flight?.date ? new Date(flight.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
      const flightRoute = flight ? `${flight.departure} → ${flight.arrival}` : '';
      
      return {
        ...b,
        booking_type: 'vol',
        item_title: flightTitle,
        item_subtitle: flightRoute,
        first_name: b.passenger_name?.split(' ')[0] || '',
        last_name: b.passenger_name?.split(' ').slice(1).join(' ') || '',
        email: b.passenger_email,
        phone: b.passenger_phone,
        nb_persons: b.num_seats,
        item_date: flightDate
      };
    }
    return b;
  });

  const filteredBookings = normalizedBookings.filter(b => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      b.first_name?.toLowerCase().includes(searchLower) ||
      b.last_name?.toLowerCase().includes(searchLower) ||
      b.email?.toLowerCase().includes(searchLower) ||
      b.item_title?.toLowerCase().includes(searchLower) ||
      b.passenger_name?.toLowerCase().includes(searchLower) ||
      b.passenger_email?.toLowerCase().includes(searchLower);
    
    const bookingType = b.booking_type || (b.flight_id ? 'vol' : null);
    const matchesType = filterType === 'all' || bookingType === filterType;
    const matchesStatus = filterStatus === 'all' || b.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div style={{ padding: '32px' }}>
        <h1 className="hero-medium" style={{ marginBottom: '24px' }}>
          Gestion des Réservations
        </h1>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{ padding: '20px', background: 'var(--bg-primary)', border: '1px solid var(--border-light)', textAlign: 'center' }}>
            <p className="hero-medium" style={{ color: '#2c5f7d' }}>{stats.total || 0}</p>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Total</p>
          </div>
          <div style={{ padding: '20px', background: 'var(--bg-primary)', border: '1px solid var(--border-light)', textAlign: 'center' }}>
            <p className="hero-medium" style={{ color: '#e6a700' }}>{stats.pending || 0}</p>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>En attente</p>
          </div>
          <div style={{ padding: '20px', background: 'var(--bg-primary)', border: '1px solid var(--border-light)', textAlign: 'center' }}>
            <p className="hero-medium" style={{ color: '#2c5f7d' }}>{stats.confirmed || 0}</p>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Confirmées</p>
          </div>
          <div style={{ padding: '20px', background: 'var(--bg-primary)', border: '1px solid var(--border-light)', textAlign: 'center' }}>
            <p className="hero-medium" style={{ color: '#28a745' }}>{stats.paid || 0}</p>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Payées</p>
          </div>
          <div style={{ padding: '20px', background: 'var(--bg-primary)', border: '1px solid var(--border-light)', textAlign: 'center' }}>
            <p className="hero-medium" style={{ color: '#c41e3a' }}>{stats.cancelled || 0}</p>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Annulées</p>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input
              type="text"
              placeholder="Rechercher (nom, email, événement...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: '12px',
              border: '1px solid var(--border-light)',
              fontSize: '14px',
              fontFamily: 'inherit',
              background: 'var(--bg-primary)',
              minWidth: '150px'
            }}
          >
            <option value="all">Tous les types</option>
            <option value="vol">Vols</option>
            <option value="stay">Séjours</option>
            <option value="event">Événements</option>
            <option value="afterwork">After-Works</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '12px',
              border: '1px solid var(--border-light)',
              fontSize: '14px',
              fontFamily: 'inherit',
              background: 'var(--bg-primary)',
              minWidth: '150px'
            }}
          >
            <option value="all">Tous les statuts</option>
            <option value="En attente">En attente</option>
            <option value="Confirmée">Confirmée</option>
            <option value="Payée">Payée</option>
            <option value="Annulée">Annulée</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <p>Chargement...</p>
        ) : filteredBookings.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', background: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Aucune réservation trouvée
            </p>
          </div>
        ) : (
          <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-light)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <th className="body-small" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Date</th>
                  <th className="body-small" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Type</th>
                  <th className="body-small" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Événement</th>
                  <th className="body-small" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Client</th>
                  <th className="body-small" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Pers.</th>
                  <th className="body-small" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Total</th>
                  <th className="body-small" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Statut</th>
                  <th className="body-small" style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td className="body-small" style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                      {formatDate(booking.created_at)}
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: getTypeColor(booking.booking_type),
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>
                        {getTypeLabel(booking.booking_type)}
                      </span>
                    </td>
                    <td className="body-small" style={{ padding: '16px', maxWidth: '250px' }}>
                      <div style={{ fontWeight: 600 }}>{booking.item_title}</div>
                      {booking.item_subtitle && (
                        <div style={{ color: '#2c5f7d', fontSize: '12px' }}>{booking.item_subtitle}</div>
                      )}
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{booking.item_date}</div>
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      <div style={{ fontWeight: 600 }}>{booking.first_name} {booking.last_name}</div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{booking.email}</div>
                      {booking.is_member && (
                        <span style={{ color: '#2c5f7d', fontSize: '11px', fontWeight: 600 }}>MEMBRE</span>
                      )}
                    </td>
                    <td className="body-small" style={{ padding: '16px', textAlign: 'center' }}>
                      {booking.nb_persons}
                    </td>
                    <td className="body-small" style={{ padding: '16px', fontWeight: 600 }}>
                      {booking.total_price?.toLocaleString('fr-FR')} €
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      <select
                        value={booking.status}
                        onChange={(e) => updateStatus(booking.id, e.target.value)}
                        style={{
                          padding: '6px 10px',
                          border: 'none',
                          background: getStatusColor(booking.status),
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        <option value="En attente">En attente</option>
                        <option value="Confirmée">Confirmée</option>
                        <option value="Payée">Payée</option>
                        <option value="Annulée">Annulée</option>
                      </select>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setSelectedBooking(booking)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid var(--border-light)',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)'
                          }}
                          title="Voir détails"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => deleteBooking(booking.id)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid #c41e3a',
                            cursor: 'pointer',
                            color: '#c41e3a'
                          }}
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="body-small" style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
          {filteredBookings.length} réservation(s)
        </p>

        {/* Detail Modal */}
        {selectedBooking && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'var(--bg-primary)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{
                padding: '24px 32px',
                borderBottom: '1px solid var(--border-light)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h2 className="heading-1">Détails de la réservation</h2>
                <button
                  onClick={() => setSelectedBooking(null)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}
                >
                  <X size={24} color="var(--text-secondary)" />
                </button>
              </div>
              <div style={{ padding: '32px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <h3 className="heading-2" style={{ marginBottom: '8px' }}>{selectedBooking.item_title}</h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>{selectedBooking.item_date}</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Client</p>
                    <p className="body-regular" style={{ fontWeight: 600 }}>
                      {selectedBooking.first_name} {selectedBooking.last_name}
                    </p>
                  </div>
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Statut membre</p>
                    <p className="body-regular">{selectedBooking.is_member ? 'Membre' : 'Non-membre'}</p>
                  </div>
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Email</p>
                    <p className="body-regular">{selectedBooking.email}</p>
                  </div>
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Téléphone</p>
                    <p className="body-regular">{selectedBooking.phone}</p>
                  </div>
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Nombre de personnes</p>
                    <p className="body-regular">{selectedBooking.nb_persons}</p>
                  </div>
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Prix unitaire</p>
                    <p className="body-regular">{selectedBooking.unit_price?.toLocaleString('fr-FR')} €</p>
                  </div>
                </div>

                {selectedBooking.options?.with_flight && (
                  <div style={{ padding: '12px', background: 'rgba(44, 95, 125, 0.1)', marginBottom: '16px' }}>
                    <p className="body-regular" style={{ color: '#2c5f7d' }}>✈️ Avec transport en jet privé</p>
                  </div>
                )}

                {selectedBooking.notes && (
                  <div style={{ marginBottom: '16px' }}>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Notes du client</p>
                    <p className="body-regular" style={{ padding: '12px', background: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
                      {selectedBooking.notes}
                    </p>
                  </div>
                )}

                <div style={{
                  padding: '20px',
                  background: 'var(--bg-secondary)',
                  border: '2px solid #2c5f7d',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span className="heading-2">Total</span>
                  <span className="hero-medium" style={{ color: '#2c5f7d' }}>
                    {selectedBooking.total_price?.toLocaleString('fr-FR')} €
                  </span>
                </div>

                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setSelectedBooking(null)}
                    className="btn-secondary"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManageBookingsNew;
